import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  NavigationExtras,
  Router,
  RouterStateSnapshot,
  Route
} from '@angular/router';
import { environment } from '../environments/environment';
import { RestService } from './lib/RestService';
import { BitrixFile } from './lib/BitrixFile';

export class User {
  ID: number;
  LID: string;
  NAME: string;
  LOGIN: string;
  EMAIL: string;
  TITLE: string;
  XML_ID: string;
  ACTIVE: 'Y' | 'N';
  WORK_FAX: string;
  WORK_ZIP: string;
  WORK_WWW: string;
  PASSWORD: string;
  WORK_LOGO: BitrixFile;
  IS_ONLINE: 'Y' | 'N';
  LAST_NAME: string;
  WORK_CITY: string;
  WORK_NOTES: string;
  WORK_PAGER: string;
  WORK_STATE: string;
  LAST_LOGIN: Date;
  SHORT_NAME: string;
  WORK_PHONE: string;
  SECOND_NAME: string;
  TIMESTAMP_X: Date;
  LANGUAGE_ID: string;
  WORK_STREET: string;
  PERSONAL_ICQ: string;
  PERSONAL_FAX: string;
  PERSONAL_WWW: string;
  PERSONAL_ZIP: string;
  WORK_PROFILE: string;
  WORK_COUNTRY: string;
  WORK_MAILBOX: string;
  WORK_COMPANY: string;
  WORK_POSITION: string;
  PERSONAL_CITY: string;
  DATE_REGISTER: Date;
  PERSONAL_PHOTO: BitrixFile;
  PERSONAL_NOTES: string;
  PERSONAL_PHONE: string;
  PERSONAL_PAGER: string;
  PERSONAL_STATE: string;
  PERSONAL_MOBILE: string;
  PERSONAL_STREET: string;
  WORK_DEPARTMENT: string;
  PERSONAL_GENDER: 'M' | 'F';
  TIME_ZONE_OFFSET: number;
  PERSONAL_MAILBOX: string;
  EXTERNAL_AUTH_ID: string;
  PERSONAL_COUNTRY: string;
  PERSONAL_BIRTHDAY: Date;
  PERSONAL_PROFESSION: string;
}

@Injectable()
export class UserService extends RestService {
  user: User | null = null;
  getCurrentPromise: Promise<User> = null;
  onSignIn: Function[] = [];
  onSignOut: Function[] = [];

  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {
    super();
    this.getCurrentPromise = this.getCurrent()
      .then((user: User) => {
        this.getCurrentPromise = null;
        return user;
      })
      .catch(() => {
        this.getCurrentPromise = null;
        return null;
      });
  }

  public async getCurrent(): Promise<User | never> {
    try {
      this.user = await this.http.get<User>(`${this.apiUrl}shop/User/me`, {
        withCredentials: true
      }).toPromise().catch((e) => {
        if (e.error.error && e.error.error.code === 'Unauthorized') return null;
      });
      // this.onSignIn.forEach((cb) => cb());
      return this.user;
    } catch (e) {
      if (e.error.error && e.error.error.code === 'Unauthorized') return null;
      this.handleError(e);
    }
  }

  public async signIn(data: {LOGIN: string; PASSWORD: string; REMEMBER?: string}): Promise<User | never> {
    try {
      this.user = await this.http.post<User>(`${this.apiUrl}shop/User/login`, data, {
        withCredentials: true,
      }).toPromise();
      return this.user;
    } catch (e) {
      this.handleError(e);
    }
  }

  public async signOut(): Promise<null | never> {
    try {
      await this.http.get<any>(`${this.apiUrl}shop/User/logout`, {
        withCredentials: true,
      }).toPromise();
      // this.onSignOut.forEach((cb) => cb());
      this.user = null;
      return null;
    } catch (e) {
      this.handleError(e);
    }
  }

  public async signUp(
    data: {
      name: {
        first: string;
        last: string;
      };
      email: string;
      password: string;
      orgName?: string;
      legalAddress?: string;
      checkingAccount?: string;
      inn?: string;
      kpp?: string;
    }
  ): Promise<User | never> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = {
      headers,
      withCredentials: true,
    };
    try {
      const res = await this.http.post<any>(`${this.apiUrl}cabinet/User`, data, options).toPromise();
      return res;
    } catch (e) {
      this.handleError(e);
    }
  }
}

@Injectable()
export class UserGuardService implements CanActivate, CanActivateChild, CanLoad {
  constructor(private userService: UserService, private router: Router) {}

  private checkPermissions(url: string) {
    // check for user
    if (!this.userService.user) {
      const redirectCommands: any[] = ['/'];
      const redirectExtras: NavigationExtras = {};
      if (url.startsWith('/personal')) {
        redirectCommands.push({outlets: {modal: ['signin']}});
        redirectExtras.queryParams = {from: url};
      }

      this.router.navigate(redirectCommands, redirectExtras);
      return false;
    }

    return true;
  }

  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    if (this.userService.getCurrentPromise) {
      return this.userService.getCurrentPromise.then(() => {
        return this.checkPermissions(state.url);
      }).catch(() => {
        return this.checkPermissions(state.url);
      });
    }

    return new Promise((resolve) => resolve(this.checkPermissions(state.url)));
  }

  public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.canActivate(route, state);
  }

  public canLoad(route: Route): Promise<boolean> {
    const url = `/${route.path}`;
    if (this.userService.getCurrentPromise) {
      return this.userService.getCurrentPromise.then(() => {
        return this.checkPermissions(url);
      }).catch(() => {
        return this.checkPermissions(url);
      });
    }

    return new Promise((resolve) => resolve(this.checkPermissions(url)));
  }
}
